import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function TransportTypeConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);
    // Keep identity stable to avoid unnecessary re-renders/refetches in MTable
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize CRUD route to avoid string duplication
    const CRUD_ROUTE = '/home/transport-types/transportTypeConfigCrud';

    //========================================================================================================
    // openTransportTypeCrud:
    // - Stable (useCallback) action handler consumed by MTables replaceFunctions.
    // - Persists 'operation' into the global state and, for non-create ops, also stores 'TransportTypeLine'.
    // - Validates the presence of a row when required and surfaces a localized error if missing.
    // - Builds a predictable tab label using the Name field (falls back gracefully).
    // - Uses a centralized CRUD route constant to keep navigation consistent.
    //========================================================================================================
    const openTransportTypeCrud = useCallback(
        (operation, row, label) => {

            // Persist the current operation in global state
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // For Edit/View, ensure a row/transport type data is selected; show error if not
            if (operation !== 'Create') {
                if (!row) { // Validate row presence
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'TransportTypeLine', value: row })); // Store selected row in the global state
            }

            // Construct tab label: "Create" uses base label; others append the Name field for context
            const tabLabel = operation === 'Create'
                ? label
                : `${label} "${row?.Name ?? ''}"`;

            onCreateTab(CRUD_ROUTE, tabLabel); // Navigate to the centralized CRUD route
        },
        [dispatch, onCreateTab, toastMessages, t]
    );

    //================================================================================================
    // replaceFunctions:
    // - Memoized Map with stable identity to avoid triggering MTable effects unnecessarily.
    // - Delegates to openTransportTypeCrud with localized labels for each operation.
    //================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openTransportTypeCrud('View', row, t('LabelViewTransportType'))],
                ['Create', () => openTransportTypeCrud('Create', null, t('LabelNewTransportType'))],
                ['Edit', (row) => openTransportTypeCrud('Edit', row, t('LabelEditTransportType'))],
            ]),
        [openTransportTypeCrud, t]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }} className="titleSettings">{t("TransportTypes")}</div>
                    <div className='labelSubtitleSettings'>{t("Manage the list of transport types available.")}</div>
                </div>
            </div>
            <MTable // Dynamic Table for Transport Types 
                filtersOnTable
                entityName={'TransportTypes'}
                roleName={'Accounts'} // Permissions tied to Accounts
                rolePermissions={roles?.map(vl => vl.Value)} // Extract permission values
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Use stable replaceFunctions map
            />
        </div>
    );
}

TransportTypeConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
